/**
 * Comprehensive API Monitoring Service
 * Collects metrics for success rates, response times, fallback usage, and production health
 */

import { APIErrorCategory, ErrorSeverity, APIErrorInfo } from './error-handler';

const isTestEnv =
  typeof process !== 'undefined' &&
  (process.env['NODE_ENV'] === 'test' || process.env['VITEST'] !== undefined);

/**
 * API request metrics
 */
export interface APIRequestMetrics {
  endpoint: string;
  method: string;
  status: 'success' | 'error' | 'fallback';
  responseTime: number;
  timestamp: Date;
  statusCode?: number;
  errorCategory?: APIErrorCategory;
  fallbackUsed: boolean;
  retryCount: number;
  cacheHit?: boolean;
  userId?: string;
  establishmentId?: string;
}

/**
 * Aggregated endpoint statistics
 */
export interface EndpointStats {
  endpoint: string;
  totalRequests: number;
  successCount: number;
  errorCount: number;
  fallbackCount: number;
  successRate: number;
  fallbackRate: number;
  avgResponseTime: number;
  p95ResponseTime: number;
  p99ResponseTime: number;
  lastError?: APIErrorInfo;
  lastSuccess?: Date;
  responseTimes: number[];
  errorsByCategory: Record<APIErrorCategory, number>;
  hourlyStats: Record<string, { requests: number; errors: number; fallbacks: number }>;
}

/**
 * System-wide health metrics
 */
export interface SystemHealthMetrics {
  overall: {
    totalRequests: number;
    successRate: number;
    fallbackRate: number;
    avgResponseTime: number;
    activeCircuitBreakers: number;
  };
  byEndpoint: Record<string, EndpointStats>;
  errors: {
    total: number;
    byCategory: Record<APIErrorCategory, number>;
    bySeverity: Record<ErrorSeverity, number>;
    recent: APIErrorInfo[];
  };
  performance: {
    slowestEndpoints: Array<{ endpoint: string; averageTime: number }>;
    fastestEndpoints: Array<{ endpoint: string; averageTime: number }>;
    highErrorRateEndpoints: Array<{ endpoint: string; errorRate: number }>;
  };
  trends: {
    hourly: Record<string, { requests: number; errors: number; fallbacks: number; avgResponseTime: number }>;
    daily: Record<string, { requests: number; errors: number; fallbacks: number; avgResponseTime: number }>;
  };
}

/**
 * Alert configuration
 */
export interface AlertConfig {
  errorRateThreshold: number; // Percentage (0-100)
  responseTimeThreshold: number; // Milliseconds
  fallbackRateThreshold: number; // Percentage (0-100)
  circuitBreakerAlerts: boolean;
  notificationChannels: ('console' | 'toast' | 'webhook')[];
  webhookUrl?: string;
}

/**
 * Alert information
 */
export interface Alert {
  id: string;
  type: 'error_rate' | 'response_time' | 'fallback_rate' | 'circuit_breaker' | 'system_health';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  endpoint?: string;
  metric: string;
  value: number;
  threshold: number;
  timestamp: Date;
  acknowledged: boolean;
}

/**
 * API Monitoring Service
 */
export class APIMonitoringService {
  private static instance: APIMonitoringService;
  private metrics: APIRequestMetrics[] = [];
  private endpointStats: Map<string, EndpointStats> = new Map();
  private alerts: Alert[] = [];
  private alertConfig: AlertConfig;
  private readonly MAX_METRICS_HISTORY = 10000; // Keep last 10k metrics
  private readonly MAX_RESPONSE_TIMES = 1000; // Keep last 1k response times per endpoint
  private readonly ALERT_NOTIFICATION_COOLDOWN_MS = 300000; // 5 minutes between same toast notification
  private lastNotifiedAlerts: Map<string, number> = new Map(); // alertKey -> timestamp of last notification

  constructor(alertConfig?: Partial<AlertConfig>) {
    this.alertConfig = {
      errorRateThreshold: 10, // 10% error rate threshold
      responseTimeThreshold: 5000, // 5 second response time threshold
      fallbackRateThreshold: 20, // 20% fallback rate threshold
      circuitBreakerAlerts: true,
      // Only log to console - toast notifications for monitoring alerts are noise for end users
      // These metrics are for developers/monitoring dashboards, not user-facing
      notificationChannels: ['console'],
      ...alertConfig
    };

    // Start background tasks
    if (!isTestEnv) {
      this.startPeriodicTasks();
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance(): APIMonitoringService {
    if (!APIMonitoringService.instance) {
      APIMonitoringService.instance = new APIMonitoringService();
    }
    return APIMonitoringService.instance;
  }

  /**
   * Record a successful API request
   */
  recordSuccess(
    endpoint: string,
    method: string,
    responseTime: number,
    options?: {
      statusCode?: number;
      cacheHit?: boolean;
      userId?: string;
      establishmentId?: string;
    }
  ): void {
    const metrics: APIRequestMetrics = {
      endpoint,
      method,
      status: 'success',
      responseTime,
      timestamp: new Date(),
      fallbackUsed: false,
      retryCount: 0,
      ...options
    };

    this.addMetrics(metrics);
    this.updateEndpointStats(metrics);
    this.checkAlerts(endpoint);
  }

  /**
   * Record an API error
   */
  recordError(
    endpoint: string,
    method: string,
    errorInfo: APIErrorInfo,
    responseTime: number,
    options?: {
      retryCount?: number;
      userId?: string;
      establishmentId?: string;
    }
  ): void {
    const metrics: APIRequestMetrics = {
      endpoint,
      method,
      status: 'error',
      responseTime,
      timestamp: new Date(),
      statusCode: typeof errorInfo.code === 'number' ? errorInfo.code : undefined,
      errorCategory: errorInfo.category,
      fallbackUsed: false,
      retryCount: options?.retryCount || 0,
      userId: options?.userId,
      establishmentId: options?.establishmentId
    };

    this.addMetrics(metrics);
    this.updateEndpointStats(metrics, errorInfo);
    this.checkAlerts(endpoint);
  }

  /**
   * Record a fallback API usage
   */
  recordFallback(
    endpoint: string,
    method: string,
    responseTime: number,
    options?: {
      originalError?: APIErrorInfo;
      retryCount?: number;
      userId?: string;
      establishmentId?: string;
    }
  ): void {
    const metrics: APIRequestMetrics = {
      endpoint,
      method,
      status: 'fallback',
      responseTime,
      timestamp: new Date(),
      fallbackUsed: true,
      retryCount: options?.retryCount || 0,
      errorCategory: options?.originalError?.category,
      userId: options?.userId,
      establishmentId: options?.establishmentId
    };

    this.addMetrics(metrics);
    this.updateEndpointStats(metrics, options?.originalError);
    this.checkAlerts(endpoint);
  }

  /**
   * Add metrics to storage with size management
   */
  private addMetrics(metrics: APIRequestMetrics): void {
    this.metrics.push(metrics);

    // Keep only recent metrics to prevent memory issues
    if (this.metrics.length > this.MAX_METRICS_HISTORY) {
      this.metrics = this.metrics.slice(-this.MAX_METRICS_HISTORY);
    }
  }

  /**
   * Update endpoint statistics
   */
  private updateEndpointStats(metrics: APIRequestMetrics, errorInfo?: APIErrorInfo): void {
    const { endpoint } = metrics;
    let stats = this.endpointStats.get(endpoint);

    if (!stats) {
      stats = {
        endpoint,
        totalRequests: 0,
        successCount: 0,
        errorCount: 0,
        fallbackCount: 0,
        successRate: 0,
        fallbackRate: 0,
        avgResponseTime: 0,
        p95ResponseTime: 0,
        p99ResponseTime: 0,
        responseTimes: [],
        errorsByCategory: {} as Record<APIErrorCategory, number>,
        hourlyStats: {}
      };
      this.endpointStats.set(endpoint, stats);
    }

    // Update counters
    stats.totalRequests++;
    
    switch (metrics.status) {
      case 'success':
        stats.successCount++;
        stats.lastSuccess = metrics.timestamp;
        break;
      case 'error':
        stats.errorCount++;
        if (errorInfo) {
          stats.lastError = errorInfo;
          stats.errorsByCategory[errorInfo.category] = 
            (stats.errorsByCategory[errorInfo.category] || 0) + 1;
        }
        break;
      case 'fallback':
        stats.fallbackCount++;
        if (errorInfo) {
          stats.errorsByCategory[errorInfo.category] = 
            (stats.errorsByCategory[errorInfo.category] || 0) + 1;
        }
        break;
    }

    // Update response times
    stats.responseTimes.push(metrics.responseTime);
    if (stats.responseTimes.length > this.MAX_RESPONSE_TIMES) {
      stats.responseTimes = stats.responseTimes.slice(-this.MAX_RESPONSE_TIMES);
    }

    // Recalculate rates and response time metrics
    stats.successRate = (stats.successCount / stats.totalRequests) * 100;
    stats.fallbackRate = (stats.fallbackCount / stats.totalRequests) * 100;
    stats.avgResponseTime = stats.responseTimes.reduce((a, b) => a + b, 0) / stats.responseTimes.length;
    
    // Calculate percentiles
    const sortedTimes = [...stats.responseTimes].sort((a, b) => a - b);
    const p95Index = Math.floor(sortedTimes.length * 0.95);
    const p99Index = Math.floor(sortedTimes.length * 0.99);
    stats.p95ResponseTime = sortedTimes[p95Index] || 0;
    stats.p99ResponseTime = sortedTimes[p99Index] || 0;

    // Update hourly stats
    const hourKey = new Date(metrics.timestamp).toISOString().slice(0, 13); // YYYY-MM-DDTHH
    if (!stats.hourlyStats[hourKey]) {
      stats.hourlyStats[hourKey] = { requests: 0, errors: 0, fallbacks: 0 };
    }
    stats.hourlyStats[hourKey].requests++;
    if (metrics.status === 'error') stats.hourlyStats[hourKey].errors++;
    if (metrics.status === 'fallback') stats.hourlyStats[hourKey].fallbacks++;
  }

  /**
   * Check and generate alerts based on current metrics
   * Uses deterministic alert IDs to prevent duplicates
   */
  private checkAlerts(endpoint: string): void {
    const stats = this.endpointStats.get(endpoint);
    if (!stats || stats.totalRequests < 10) return; // Need minimum requests for reliable stats

    const newAlerts: Alert[] = [];

    // Error rate alert - use deterministic ID
    const errorRate = ((stats.errorCount + stats.fallbackCount) / stats.totalRequests) * 100;
    if (errorRate > this.alertConfig.errorRateThreshold) {
      const alertId = `error_rate_${endpoint}`;
      const existingAlert = this.alerts.find(a => a.id === alertId && !a.acknowledged);

      if (existingAlert) {
        // Update existing alert instead of creating new
        existingAlert.timestamp = new Date();
        existingAlert.value = errorRate;
        existingAlert.severity = errorRate > 50 ? 'critical' : errorRate > 25 ? 'high' : 'medium';
      } else {
        newAlerts.push({
          id: alertId,
          type: 'error_rate',
          severity: errorRate > 50 ? 'critical' : errorRate > 25 ? 'high' : 'medium',
          message: `High error rate for ${endpoint}: ${errorRate.toFixed(1)}%`,
          endpoint,
          metric: 'error_rate',
          value: errorRate,
          threshold: this.alertConfig.errorRateThreshold,
          timestamp: new Date(),
          acknowledged: false
        });
      }
    }

    // Response time alert - use deterministic ID
    if (stats.avgResponseTime > this.alertConfig.responseTimeThreshold) {
      const alertId = `response_time_${endpoint}`;
      const existingAlert = this.alerts.find(a => a.id === alertId && !a.acknowledged);

      if (existingAlert) {
        existingAlert.timestamp = new Date();
        existingAlert.value = stats.avgResponseTime;
        existingAlert.severity = stats.avgResponseTime > 10000 ? 'critical' : 'medium';
      } else {
        newAlerts.push({
          id: alertId,
          type: 'response_time',
          severity: stats.avgResponseTime > 10000 ? 'critical' : 'medium',
          message: `Slow response time for ${endpoint}: ${stats.avgResponseTime.toFixed(0)}ms`,
          endpoint,
          metric: 'response_time',
          value: stats.avgResponseTime,
          threshold: this.alertConfig.responseTimeThreshold,
          timestamp: new Date(),
          acknowledged: false
        });
      }
    }

    // Fallback rate alert - use deterministic ID
    if (stats.fallbackRate > this.alertConfig.fallbackRateThreshold) {
      const alertId = `fallback_rate_${endpoint}`;
      const existingAlert = this.alerts.find(a => a.id === alertId && !a.acknowledged);

      if (existingAlert) {
        existingAlert.timestamp = new Date();
        existingAlert.value = stats.fallbackRate;
        existingAlert.severity = stats.fallbackRate > 80 ? 'critical' : stats.fallbackRate > 50 ? 'high' : 'medium';
      } else {
        newAlerts.push({
          id: alertId,
          type: 'fallback_rate',
          severity: stats.fallbackRate > 80 ? 'critical' : stats.fallbackRate > 50 ? 'high' : 'medium',
          message: `High fallback rate for ${endpoint}: ${stats.fallbackRate.toFixed(1)}%`,
          endpoint,
          metric: 'fallback_rate',
          value: stats.fallbackRate,
          threshold: this.alertConfig.fallbackRateThreshold,
          timestamp: new Date(),
          acknowledged: false
        });
      }
    }

    // Add only new alerts and notify
    newAlerts.forEach(alert => {
      this.alerts.push(alert);
      this.notifyAlert(alert);
    });

    // Cleanup old alerts (keep only last 100)
    if (this.alerts.length > 100) {
      this.alerts = this.alerts.slice(-100);
    }
  }

  /**
   * Send alert notifications with cooldown to prevent spam
   */
  private notifyAlert(alert: Alert): void {
    // Always log to console for debugging purposes
    if (this.alertConfig.notificationChannels.includes('console')) {
      const logMethod = alert.severity === 'critical' ? 'error' :
                      alert.severity === 'high' ? 'warn' : 'log';
      console[logMethod](`[APIMonitoring] ${alert.message}`, alert);
    }

    // Toast notifications with cooldown to prevent spam
    if (this.alertConfig.notificationChannels.includes('toast')) {
      const alertKey = `${alert.type}_${alert.endpoint || 'global'}`;
      const lastNotified = this.lastNotifiedAlerts.get(alertKey);
      const now = Date.now();

      // Check if we're still in cooldown period
      if (lastNotified && (now - lastNotified) < this.ALERT_NOTIFICATION_COOLDOWN_MS) {
        // Skip toast notification - still in cooldown period
        return;
      }

      // Update last notified timestamp
      this.lastNotifiedAlerts.set(alertKey, now);

      import('react-hot-toast').then(({ toast }) => {
        if (alert.severity === 'critical' || alert.severity === 'high') {
          // Use deterministic toast ID to replace existing toast instead of stacking
          const toastId = `alert_${alert.type}_${alert.endpoint || 'global'}`;
          toast.error(`${alert.message}`, { id: toastId });
        }
      }).catch(() => {
        // Ignore if toast library is not available
      });
    }

    if (this.alertConfig.notificationChannels.includes('webhook') && this.alertConfig.webhookUrl) {
      fetch(this.alertConfig.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(alert)
      }).catch(error => console.error('Failed to send webhook alert:', error));
    }
  }

  /**
   * Get comprehensive system health metrics
   */
  getSystemHealth(): SystemHealthMetrics {
    const overallStats = this.calculateOverallStats();
    const endpointStatsObject: Record<string, EndpointStats> = {};
    
    this.endpointStats.forEach((stats, endpoint) => {
      endpointStatsObject[endpoint] = { ...stats };
    });

    const errorsByCategory = this.calculateErrorsByCategory();
    const errorsBySeverity = this.calculateErrorsBySeverity();
    const recentErrors = this.getRecentErrors();

    return {
      overall: overallStats,
      byEndpoint: endpointStatsObject,
      errors: {
        total: recentErrors.length,
        byCategory: errorsByCategory,
        bySeverity: errorsBySeverity,
        recent: recentErrors
      },
      performance: this.calculatePerformanceMetrics(),
      trends: this.calculateTrends()
    };
  }

  /**
   * Calculate overall system statistics
   */
  private calculateOverallStats() {
    let totalRequests = 0;
    let totalSuccess = 0;
    let totalFallbacks = 0;
    let totalResponseTime = 0;
    let responseTimeCount = 0;

    this.endpointStats.forEach(stats => {
      totalRequests += stats.totalRequests;
      totalSuccess += stats.successCount;
      totalFallbacks += stats.fallbackCount;
      totalResponseTime += stats.avgResponseTime * stats.responseTimes.length;
      responseTimeCount += stats.responseTimes.length;
    });

    return {
      totalRequests,
      successRate: totalRequests > 0 ? (totalSuccess / totalRequests) * 100 : 0,
      fallbackRate: totalRequests > 0 ? (totalFallbacks / totalRequests) * 100 : 0,
      avgResponseTime: responseTimeCount > 0 ? totalResponseTime / responseTimeCount : 0,
      activeCircuitBreakers: this.getActiveCircuitBreakersCount()
    };
  }

  /**
   * Get active circuit breakers count (from error handler)
   */
  private getActiveCircuitBreakersCount(): number {
    // This would integrate with the error handler to get circuit breaker status
    return 0; // Placeholder
  }

  /**
   * Calculate errors by category
   */
  private calculateErrorsByCategory(): Record<APIErrorCategory, number> {
    const byCategory = {} as Record<APIErrorCategory, number>;
    
    Object.values(APIErrorCategory).forEach(category => {
      byCategory[category] = 0;
    });

    this.endpointStats.forEach(stats => {
      Object.entries(stats.errorsByCategory).forEach(([category, count]) => {
        byCategory[category as APIErrorCategory] += count;
      });
    });

    return byCategory;
  }

  /**
   * Calculate errors by severity (would need to track severity in metrics)
   */
  private calculateErrorsBySeverity(): Record<ErrorSeverity, number> {
    const bySeverity = {} as Record<ErrorSeverity, number>;
    
    Object.values(ErrorSeverity).forEach(severity => {
      bySeverity[severity] = 0;
    });

    // This would require storing severity in metrics
    // For now, estimate based on error categories
    const categoryErrors = this.calculateErrorsByCategory();
    
    bySeverity[ErrorSeverity.CRITICAL] = 
      (categoryErrors[APIErrorCategory.SERVER_ERROR] || 0) +
      (categoryErrors[APIErrorCategory.CIRCUIT_BREAKER] || 0);
      
    bySeverity[ErrorSeverity.HIGH] = 
      (categoryErrors[APIErrorCategory.NETWORK_ERROR] || 0) +
      (categoryErrors[APIErrorCategory.AUTHENTICATION] || 0);
      
    bySeverity[ErrorSeverity.MEDIUM] = 
      (categoryErrors[APIErrorCategory.TIMEOUT] || 0) +
      (categoryErrors[APIErrorCategory.RATE_LIMIT] || 0) +
      (categoryErrors[APIErrorCategory.AUTHORIZATION] || 0);
      
    bySeverity[ErrorSeverity.LOW] = 
      (categoryErrors[APIErrorCategory.CLIENT_ERROR] || 0) +
      (categoryErrors[APIErrorCategory.VALIDATION] || 0) +
      (categoryErrors[APIErrorCategory.NOT_FOUND] || 0);

    return bySeverity;
  }

  /**
   * Get recent errors from endpoint stats
   */
  private getRecentErrors(): APIErrorInfo[] {
    const recentErrors: APIErrorInfo[] = [];
    
    this.endpointStats.forEach(stats => {
      if (stats.lastError) {
        recentErrors.push(stats.lastError);
      }
    });

    return recentErrors
      .sort((a, b) => b.context.timestamp.getTime() - a.context.timestamp.getTime())
      .slice(0, 50); // Last 50 errors
  }

  /**
   * Calculate performance metrics
   */
  private calculatePerformanceMetrics() {
    const endpoints = Array.from(this.endpointStats.values());
    
    const slowestEndpoints = endpoints
      .sort((a, b) => b.avgResponseTime - a.avgResponseTime)
      .slice(0, 5)
      .map(stats => ({
        endpoint: stats.endpoint,
        averageTime: stats.avgResponseTime
      }));

    const fastestEndpoints = endpoints
      .sort((a, b) => a.avgResponseTime - b.avgResponseTime)
      .slice(0, 5)
      .map(stats => ({
        endpoint: stats.endpoint,
        averageTime: stats.avgResponseTime
      }));

    const highErrorRateEndpoints = endpoints
      .filter(stats => stats.totalRequests >= 10)
      .sort((a, b) => {
        const aErrorRate = ((a.errorCount + a.fallbackCount) / a.totalRequests) * 100;
        const bErrorRate = ((b.errorCount + b.fallbackCount) / b.totalRequests) * 100;
        return bErrorRate - aErrorRate;
      })
      .slice(0, 5)
      .map(stats => ({
        endpoint: stats.endpoint,
        errorRate: ((stats.errorCount + stats.fallbackCount) / stats.totalRequests) * 100
      }));

    return {
      slowestEndpoints,
      fastestEndpoints,
      highErrorRateEndpoints
    };
  }

  /**
   * Calculate trends (hourly and daily)
   */
  private calculateTrends() {
    const hourly: Record<string, { requests: number; errors: number; fallbacks: number; avgResponseTime: number }> = {};
    const daily: Record<string, { requests: number; errors: number; fallbacks: number; avgResponseTime: number }> = {};

    // Aggregate hourly stats from all endpoints
    this.endpointStats.forEach(stats => {
      Object.entries(stats.hourlyStats).forEach(([hour, hourStats]) => {
        if (!hourly[hour]) {
          hourly[hour] = { requests: 0, errors: 0, fallbacks: 0, avgResponseTime: 0 };
        }
        hourly[hour].requests += hourStats.requests;
        hourly[hour].errors += hourStats.errors;
        hourly[hour].fallbacks += hourStats.fallbacks;
      });
    });

    // Calculate daily stats from hourly
    Object.entries(hourly).forEach(([hour, hourStats]) => {
      const day = hour.slice(0, 10); // YYYY-MM-DD
      if (!daily[day]) {
        daily[day] = { requests: 0, errors: 0, fallbacks: 0, avgResponseTime: 0 };
      }
      daily[day].requests += hourStats.requests;
      daily[day].errors += hourStats.errors;
      daily[day].fallbacks += hourStats.fallbacks;
    });

    return { hourly, daily };
  }

  /**
   * Get current alerts
   */
  getAlerts(): Alert[] {
    return [...this.alerts].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  /**
   * Acknowledge an alert
   */
  acknowledgeAlert(alertId: string): boolean {
    const alert = this.alerts.find(a => a.id === alertId);
    if (alert) {
      alert.acknowledged = true;
      return true;
    }
    return false;
  }

  /**
   * Clear acknowledged alerts
   */
  clearAcknowledgedAlerts(): void {
    this.alerts = this.alerts.filter(alert => !alert.acknowledged);
  }

  /**
   * Export metrics for external analysis
   */
  exportMetrics(): {
    rawMetrics: APIRequestMetrics[];
    endpointStats: Record<string, EndpointStats>;
    alerts: Alert[];
    exportedAt: Date;
  } {
    const endpointStatsObject: Record<string, EndpointStats> = {};
    this.endpointStats.forEach((stats, endpoint) => {
      endpointStatsObject[endpoint] = { ...stats };
    });

    return {
      rawMetrics: [...this.metrics],
      endpointStats: endpointStatsObject,
      alerts: [...this.alerts],
      exportedAt: new Date()
    };
  }

  /**
   * Start periodic tasks
   */
  private startPeriodicTasks(): void {
    // Cleanup old data every hour
    setInterval(() => {
      this.cleanupOldData();
    }, 60 * 60 * 1000); // 1 hour

    // Generate system health reports every 15 minutes
    setInterval(() => {
      this.generateHealthReport();
    }, 15 * 60 * 1000); // 15 minutes
  }

  /**
   * Cleanup old data to prevent memory leaks
   */
  private cleanupOldData(): void {
    const cutoffTime = new Date(Date.now() - 24 * 60 * 60 * 1000); // 24 hours ago

    // Clean old metrics
    this.metrics = this.metrics.filter(metric => metric.timestamp > cutoffTime);

    // Clean old hourly stats from endpoint stats
    this.endpointStats.forEach(stats => {
      Object.keys(stats.hourlyStats).forEach(hour => {
        const hourDate = new Date(hour + ':00:00.000Z');
        if (hourDate < cutoffTime) {
          delete stats.hourlyStats[hour];
        }
      });
    });

    // Clean old alerts
    const alertCutoffTime = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // 7 days ago
    this.alerts = this.alerts.filter(alert => alert.timestamp > alertCutoffTime);

    // Clean old notification cooldown entries
    const notificationCutoff = Date.now() - this.ALERT_NOTIFICATION_COOLDOWN_MS;
    for (const [key, timestamp] of this.lastNotifiedAlerts.entries()) {
      if (timestamp < notificationCutoff) {
        this.lastNotifiedAlerts.delete(key);
      }
    }
  }

  /**
   * Generate periodic health reports
   */
  private generateHealthReport(): void {
    const health = this.getSystemHealth();
    
    // Log summary to console
    console.log('[APIMonitoring] System Health Report:', {
      timestamp: new Date().toISOString(),
      overall: health.overall,
      activeAlerts: this.alerts.filter(a => !a.acknowledged).length,
      endpointsCount: Object.keys(health.byEndpoint).length
    });

    // Could send to monitoring dashboard or external service
    if (typeof window !== 'undefined' && 'monitoringDashboard' in window) {
      (window as typeof window & { monitoringDashboard: { updateHealth: (health: unknown) => void } }).monitoringDashboard.updateHealth(health);
    }
  }

  /**
   * Update alert configuration
   */
  updateAlertConfig(newConfig: Partial<AlertConfig>): void {
    this.alertConfig = { ...this.alertConfig, ...newConfig };
  }

  /**
   * Check if fallback was used in recent request for specific endpoint
   */
  wasRecentFallbackUsed(endpoint: string, withinMs: number = 5000): boolean {
    const cutoffTime = Date.now() - withinMs;
    
    return this.metrics.some(metric => 
      metric.endpoint === endpoint &&
      metric.timestamp.getTime() > cutoffTime &&
      metric.fallbackUsed === true
    );
  }
}

// Export singleton instance
export const apiMonitoringService = APIMonitoringService.getInstance();

// Export default instance
export default apiMonitoringService;
